import React from "react";
import { FaPrint, FaTimes, FaRupeeSign } from "react-icons/fa";
import { amountToWordsIndian } from "../utils/numberToWords";
import receiptSaiLogo from "../assets/receipt-sai-logo.png";
import "../styles/DonationReceipt.css";

const DonationReceipt = ({ donation, isOpen = true, onClose }) => {
  if (!isOpen || !donation) return null;

  // Format Date to DD/MM/YYYY
  const formatDate = (dateString) => {
    if (!dateString) {
      const d = new Date();
      return `${String(d.getDate()).padStart(2, "0")}/${String(
        d.getMonth() + 1
      ).padStart(2, "0")}/${d.getFullYear()}`;
    }
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return String(dateString);
    return `${String(d.getDate()).padStart(2, "0")}/${String(
      d.getMonth() + 1
    ).padStart(2, "0")}/${d.getFullYear()}`;
  };

  const donorName =
    donation.donorName || donation.donarName || donation.name || "Devotee";
  const email = donation.email || "";
  const address = donation.address || "Ambattur, Chennai";
  const amountNumber = Number(donation.amount || 0);
  const amountWords = amountToWordsIndian(amountNumber);
  const sNo =
    donation.receiptNumber ||
    donation.sNo ||
    (donation._id ? String(donation._id).slice(-4).toUpperCase() : "7900");
  const receiptDate = formatDate(donation.createdAt || donation.date);

  // Payment method & UTR
  const methodStr = donation.paymentMethod || "QR Code / UPI";
  let cashChequeText = methodStr;
  if (donation.transactionId) {
    cashChequeText = `${methodStr} (${donation.transactionId})`;
  }

  const drawnOnText =
    donation.drawnOn ||
    (methodStr.toLowerCase().includes("cash")
      ? "Direct Seva"
      : "Google Pay / UPI");

  // Determine active purpose among the 5 receipt categories
  const rawPurpose = String(donation.purpose || "General Donation").toLowerCase();
  let activeCat = "GENERAL DONATION";
  if (rawPurpose.includes("annadanam") || rawPurpose.includes("food")) {
    activeCat = "ANNADANAM";
  } else if (
    rawPurpose.includes("abhishekam") ||
    rawPurpose.includes("pooja") ||
    rawPurpose.includes("homam")
  ) {
    activeCat = "ABHISHEKAM";
  } else if (rawPurpose.includes("corpus")) {
    activeCat = "CORPUS FUND";
  } else if (
    rawPurpose.includes("prayer") ||
    rawPurpose.includes("hall") ||
    rawPurpose.includes("building") ||
    rawPurpose.includes("maintain")
  ) {
    activeCat = "PRAYER HALL FUND";
  } else {
    activeCat = "GENERAL DONATION";
  }

  const handlePrint = () => {
    const printArea = document.getElementById("mandir-receipt-slip-print");
    if (!printArea) {
      window.print();
      return;
    }

    const oldFrame = document.getElementById("donation-print-iframe");
    if (oldFrame) {
      oldFrame.remove();
    }

    const printFrame = document.createElement("iframe");
    printFrame.id = "donation-print-iframe";
    printFrame.style.position = "fixed";
    printFrame.style.top = "0";
    printFrame.style.left = "0";
    printFrame.style.width = "100vw";
    printFrame.style.height = "100vh";
    printFrame.style.border = "none";
    printFrame.style.opacity = "0.01";
    printFrame.style.pointerEvents = "none";
    printFrame.style.zIndex = "-99999";
    document.body.appendChild(printFrame);

    const doc = printFrame.contentDocument || printFrame.contentWindow.document;

    let stylesHtml = "";
    document.querySelectorAll('style, link[rel="stylesheet"]').forEach((node) => {
      stylesHtml += node.outerHTML;
    });

    doc.open();
    doc.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <base href="${window.location.origin}/" />
          <title>Donation Receipt - Sri Gnana Sai Baba Mandir</title>
          <meta charset="utf-8" />
          ${stylesHtml}
          <style>
            @page {
              size: A4 portrait;
              margin: 6mm 10mm;
            }
            *, *::before, *::after {
              box-sizing: border-box;
              visibility: visible !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            html, body {
              background: #ffffff !important;
              color: #0f2d5c !important;
              margin: 0 !important;
              padding: 0 !important;
              width: 100% !important;
              height: auto !important;
              overflow: visible !important;
            }
            body, body * {
              visibility: visible !important;
            }
            .mandir-receipt-slip,
            #mandir-receipt-slip-print,
            #mandir-receipt-slip-print * {
              visibility: visible !important;
            }
            .mandir-receipt-slip {
              width: 100% !important;
              max-width: 720px !important;
              box-shadow: none !important;
              border: 2.5px solid #0f2d5c !important;
              margin: 0 auto !important;
              page-break-inside: avoid !important;
              break-inside: avoid !important;
            }
            @media print {
              *, *::before, *::after {
                visibility: visible !important;
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
              body, body * {
                visibility: visible !important;
              }
              .mandir-receipt-slip,
              #mandir-receipt-slip-print,
              #mandir-receipt-slip-print * {
                visibility: visible !important;
              }
            }
          </style>
        </head>
        <body>
          <div style="width: 100%; display: flex; justify-content: center; padding: 10px 0;">
            ${printArea.outerHTML}
          </div>
        </body>
      </html>
    `);
    doc.close();

    const triggerPrint = () => {
      try {
        printFrame.contentWindow.focus();
        printFrame.contentWindow.print();
      } catch (err) {
        console.error("Print error:", err);
      }
    };

    setTimeout(() => {
      const imgs = doc.images;
      if (!imgs || imgs.length === 0) {
        triggerPrint();
      } else {
        let loaded = 0;
        const total = imgs.length;
        let fired = false;
        const checkDone = () => {
          if (!fired) {
            fired = true;
            setTimeout(triggerPrint, 150);
          }
        };

        const handleImageEvent = () => {
          loaded++;
          if (loaded >= total) checkDone();
        };

        for (let i = 0; i < total; i++) {
          if (imgs[i].complete && imgs[i].naturalWidth !== 0) {
            loaded++;
          } else {
            imgs[i].onload = handleImageEvent;
            imgs[i].onerror = handleImageEvent;
          }
        }
        if (loaded >= total) {
          checkDone();
        } else {
          setTimeout(checkDone, 450);
        }
      }
    }, 150);
  };

  return (
    <div className="receipt-modal-backdrop">
      <div
        className="receipt-modal-card"
      >
        {/* Modal Action Header (hidden during print) */}
        <div className="receipt-action-bar">
          <div className="receipt-action-title">
            <FaRupeeSign /> Sri Gnana Saibaba Mandir Official Donation Receipt
          </div>
          <div className="receipt-action-btns">
            <button
              className="receipt-btn receipt-btn-print"
              onClick={handlePrint}
              title="Print receipt or Save as PDF"
            >
              <FaPrint /> Print Receipt
            </button>
            {onClose && (
              <button
                className="receipt-btn receipt-btn-close"
                onClick={onClose}
                title="Close receipt preview"
              >
                <FaTimes /> Close
              </button>
            )}
          </div>
        </div>

        {/* Printable Receipt Container */}
        <div className="receipt-slip-container">
          <div className="mandir-receipt-slip" id="mandir-receipt-slip-print">
            {/* Header: Sai Baba Emblem + Mandir Title & Details */}
            <div className="receipt-header-row">
              <div className="receipt-logo-wrap">
                <img
                  src={receiptSaiLogo}
                  alt="Sri Gnana Sai Baba Emblem"
                  className="receipt-sai-emblem"
                />
              </div>

              <div className="receipt-header-text">
                <h1 className="receipt-mandir-title">
                  SRI GNANA SAIBABA MANDIR
                </h1>
                <p className="receipt-mandir-address">
                  Ragavendra Cross Street, Erikarai Road,
                  <br />
                  Vekateswara Nagar, Ambattur, Chennai - 600 053.
                </p>
                <p className="receipt-mandir-cell">
                  Cell : 98412 45070, 99414 25158
                </p>
              </div>
            </div>

            {/* S.No, RECEIPT, Date */}
            <div className="receipt-meta-row">
              <div className="receipt-sno-box">
                <span>S.No.</span>
                <span className="receipt-sno-value">{sNo}</span>
              </div>

              <div className="receipt-center-title">RECEIPT</div>

              <div className="receipt-date-box">
                <span>Date :</span>
                <span className="receipt-date-value">{receiptDate}</span>
              </div>
            </div>

            {/* Main Content Lines */}
            <div className="receipt-body">
              {/* Line 1: Received with Thanks from */}
              <div className="receipt-field-line">
                <span className="receipt-field-label">
                  Received with Thanks from
                </span>
                <span className="receipt-field-fill">{donorName}</span>
              </div>

              {/* Email Line */}
              {email ? (
                <div className="receipt-field-line">
                  <span className="receipt-field-label">Email</span>
                  <span className="receipt-field-fill">{email}</span>
                </div>
              ) : null}

              {/* Line 2: Address */}
              <div className="receipt-field-line">
                <span className="receipt-field-label">Address</span>
                <span className="receipt-field-fill">{address}</span>
              </div>

              {/* Line 3: Sum of Rupees */}
              <div className="receipt-field-line">
                <span className="receipt-field-label">Sum of Rupees</span>
                <span className="receipt-field-fill">{amountWords}</span>
              </div>

              {/* Line 4: Cash / Cheque ... drawn on ... towards CORPUS FUND / */}
              <div className="receipt-payment-row">
                <span>Cash / Cheque</span>
                <span className="receipt-fill-inline">{cashChequeText}</span>
                <span>drawn on</span>
                <span className="receipt-fill-inline">{drawnOnText}</span>
                <span>towards</span>
                <span
                  className={`receipt-cat-item ${
                    activeCat === "CORPUS FUND" ? "active-category" : ""
                  }`}
                >
                  CORPUS FUND /
                </span>
              </div>

              {/* Line 5: ABHISHEKAM / ANNADANAM / PRAYER HALL FUND / GENERAL DONATION. */}
              <div className="receipt-categories-row">
                <span
                  className={`receipt-cat-item ${
                    activeCat === "ABHISHEKAM" ? "active-category" : ""
                  }`}
                >
                  ABHISHEKAM
                </span>
                <span>/</span>

                <span
                  className={`receipt-cat-item ${
                    activeCat === "ANNADANAM" ? "active-category" : ""
                  }`}
                >
                  ANNADANAM
                </span>
                <span>/</span>

                <span
                  className={`receipt-cat-item ${
                    activeCat === "PRAYER HALL FUND" ? "active-category" : ""
                  }`}
                >
                  PRAYER HALL FUND
                </span>
                <span>/</span>

                <span
                  className={`receipt-cat-item ${
                    activeCat === "GENERAL DONATION" ? "active-category" : ""
                  }`}
                >
                  GENERAL DONATION.
                </span>
              </div>
            </div>

            {/* Bottom Row: Rs. Amount + Trust Signatory */}
            <div className="receipt-footer-row">
              <div className="receipt-rs-box">
                <span className="receipt-rs-label">Rs.</span>
                <span className="receipt-rs-amount">
                  {amountNumber ? `${amountNumber.toLocaleString("en-IN")}/-` : ""}
                </span>
              </div>

              <div className="receipt-trust-sign-block">
                <div className="receipt-trust-name">
                  For Sri Gnana Saibaba Thiyana Nilayam Charitable Trust
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DonationReceipt;
